import { useEffect } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { Site } from '@datadog/browser-logs/node_modules/@datadog/browser-core/cjs/domain/configuration/intakeSites'
import { datadogRum } from '@datadog/browser-rum'
import type { PropagatorType } from '@datadog/browser-rum-core/cjs/domain/tracing/tracer.types'
import { trackEvent } from '@intercom/messenger-js-sdk'
import { ConfigProvider } from 'antd'
import locale from 'antd/lib/locale/en_GB'
import { polyfill } from 'interweave-ssr'
// eslint-disable-next-line import/order
import { v4 as uuidv4 } from 'uuid'
import 'leaflet/dist/leaflet.css'
import dynamic from 'next/dynamic'
import 'react-resizable/css/styles.css'
import { useRouter } from 'next/router'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import 'src/locales/i18n'

import config from '../config'

import { AIGlowEffect } from '@/components/shared-components/BUI/AiGlowEffect'
import ErrorBoundary from '@/components/util-components/ErrorBoundary'
import GlobalMetadata from '@/components/util-components/GlobalMetadata'
import { AIActivityProvider } from '@/context/aiActivityContext'
import { ConfirmProvider } from '@/context/confirmContext'
import { ShedProvider } from '@/context/shedContext'
import { BuildPassThemeProvider } from '@/context/themeContext'
import { WhiteLabelProvider } from '@/context/whiteLabelContext'
import { useAntDPickerAutoOk } from '@/hooks/useAntDPickerAutoOk'
import { AuthenticatedLayout } from '@/layouts/AuthenticatedLayout'

import { Globals } from 'src/styles/Globals'

import '@/utils/pollyfills'
import 'antd/dist/antd.less'

import '@fontsource/geist/400.css'
import '@fontsource/geist/500.css'
import '@fontsource/geist/600.css'

const AutoPWABlocker = dynamic(
  () => import('@/components/util-components/AutoPWABlocker'),
  { ssr: false }
)

polyfill()

datadogLogs.init({
  clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  site: process.env.NEXT_PUBLIC_DATADOG_SITE as Site,
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
  service: config.service,
  env: config.environment,
  version: config.version,
  proxy: config.appHost + '/api/datadog',
  beforeSend: (event) => {
    event['path'] = window.location.pathname
    event['env'] = config.environment
    event['environment'] = config.environment
    event['version'] = config.version
    event['branch'] = config.branch
    event['locale'] = config.defaultLocale
    event['userId'] = datadogRum.getUser()?.id
    event['requestId'] = uuidv4()
    return true
  },
})

datadogRum.init({
  applicationId: '313f6e7f-61db-4132-af59-56f1a19a10e9',
  clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  service: config.service,
  env: config.environment,
  site: process.env.NEXT_PUBLIC_DATADOG_SITE,
  version: config.version,
  proxy: config.appHost + '/api/datadog',
  trackUserInteractions: true,
  allowedTracingUrls: [
    { match: config.apiEndpoint, propagatorTypes: ['tracecontext'] },
    {
      match: /https:\/\/.*\.preview\.buildpass\.com\.au/,
      propagatorTypes: ['tracecontext'],
    },
    ...(process.env.NODE_ENV === 'development'
      ? [
          {
            match: 'http://localhost:8911/',
            propagatorTypes: ['tracecontext'] as PropagatorType[],
          },
          {
            match: 'http://localhost:3000/',
            propagatorTypes: ['tracecontext'] as PropagatorType[],
          },
        ]
      : []),
  ],
  trackResources: true,
  traceSampleRate: 100,
  defaultPrivacyLevel: 'mask-user-input',
  sessionSampleRate: 15,
  sessionReplaySampleRate: 15,
  trackLongTasks: true,
  beforeSend: (event) => {
    event['path'] = window.location.pathname
    event['env'] = config.environment
    event['environment'] = config.environment
    event['version'] = config.version
    event['branch'] = config.branch
    event['locale'] = config.defaultLocale
    event['userId'] = datadogRum.getUser()?.id
    event['requestId'] = uuidv4()
    return true
  },
})

// To test locally, comment out the api host and comment out the development check below. Events will be loaded in the console.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'development') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') posthog.debug()
    },
    // Autocapture all button clicks
    autocapture: {
      dom_event_allowlist: ['click'],
      // url_allowlist: ['posthog.com./docs/.*'], // strings or RegExps
      element_allowlist: ['button', 'a'], // DOM elements from this list ['a', 'button', 'form', 'input', 'select', 'textarea', 'label']
      // css_selector_allowlist: ['.ph-trackable-element'], // Capture elements with our custom CSS class
      // element_attribute_ignorelist: ['data-attr-pii="email"'], // List of element attributes to ignore
    },
    capture_pageleave: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // Highly recommended as a minimum!!
        // color: false,
        // date: false,
        // 'datetime-local': false,
        // email: false,
        // month: false,
        // number: false,
        // range: false,
        // search: false,
        // tel: false,
        // text: false,
        // time: false,
        // url: false,
        // week: false,
        // textarea: false,
        // select: false,
      },
    },
  })
}

const App = ({ Component, pageProps }) => {
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = async () => {
      trackEvent('page-viewed', {
        url: window.location.href,
        title: document.title,
      })
      posthog?.capture('$pageview')
      datadogLogs.logger.info('Route changed', {
        path: window.location.pathname,
      })
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  const Layout = Component.getLayout || AuthenticatedLayout

  // @TODO: Move this to a more proper location
  useAntDPickerAutoOk()

  return (
    <ErrorBoundary>
      <ConfigProvider locale={locale}>
        <ShedProvider>
          <AutoPWABlocker />
          <GlobalMetadata />
          <BuildPassThemeProvider>
            <WhiteLabelProvider>
              <AIActivityProvider>
                <AIGlowEffect />
                <PostHogProvider client={posthog}>
                  <Layout>
                    <Globals />
                    <ConfirmProvider>
                      <Component {...pageProps} />
                    </ConfirmProvider>
                  </Layout>
                </PostHogProvider>
              </AIActivityProvider>
            </WhiteLabelProvider>
          </BuildPassThemeProvider>
        </ShedProvider>
      </ConfigProvider>
    </ErrorBoundary>
  )
}

export default App
